import { AxiosError } from 'axios';
import { differenceInCalendarDays, startOfDay } from 'date-fns';
import type {
  AuthSubscriptionDetails,
  AuthSubscriptionStatus,
  AuthSubscriptionStatusResponse,
} from '@/types/auth';

export const SUBSCRIPTION_REQUIRED_ROUTE = '/auth/subscription-required';
export const SUBSCRIPTION_GATE_STORAGE_KEY = 'caloriescience_subscription_gate';
export const SUBSCRIPTION_STORAGE_KEY = 'caloriescience_subscription';
export const SUBSCRIPTION_ACCESS_BLOCKED_EVENT = 'caloriescience:subscription-access-blocked';

export const DEFAULT_SUBSCRIPTION_BLOCKED_MESSAGE =
  'Your trial has ended. Please contact info@caloriescience.ai to continue using CalorieScience.';
export const DEFAULT_SUBSCRIPTION_SUPPORT_EMAIL = 'info@caloriescience.ai';
export const SUBSCRIPTION_EXPIRY_WARNING_DAYS = 7;

export interface SubscriptionExpiryWarning {
  daysRemaining: number;
  endsAt: string;
  supportEmail: string;
}

type SubscriptionExpirySource = Pick<
  AuthSubscriptionDetails,
  'endsAt' | 'allowed' | 'enforcementEnabled'
> | null | undefined;

export const getSubscriptionExpiryWarning = (
  subscription: SubscriptionExpirySource,
  options?: {
    warningDays?: number;
    accessBlocked?: boolean;
  },
): SubscriptionExpiryWarning | null => {
  if (options?.accessBlocked || !subscription?.endsAt) {
    return null;
  }

  if (subscription.allowed === false && subscription.enforcementEnabled) {
    return null;
  }

  const endDate = startOfDay(new Date(subscription.endsAt));
  if (Number.isNaN(endDate.getTime())) {
    return null;
  }

  const daysRemaining = differenceInCalendarDays(endDate, startOfDay(new Date()));
  const warningDays = options?.warningDays ?? SUBSCRIPTION_EXPIRY_WARNING_DAYS;

  if (daysRemaining < 0 || daysRemaining > warningDays) {
    return null;
  }

  return {
    daysRemaining,
    endsAt: subscription.endsAt,
    supportEmail: DEFAULT_SUBSCRIPTION_SUPPORT_EMAIL,
  };
};

export const formatSubscriptionExpiryWarningMessage = (
  warning: SubscriptionExpiryWarning,
): string => {
  if (warning.daysRemaining === 0) {
    return 'Your CalorieScience subscription ends today.';
  }

  if (warning.daysRemaining === 1) {
    return 'Your CalorieScience subscription ends tomorrow.';
  }

  return `Your CalorieScience subscription ends in ${warning.daysRemaining} days.`;
};

export interface SubscriptionGateState {
  accessBlocked: boolean;
  reason: string | null;
  message: string | null;
  supportEmail: string | null;
  subscription: AuthSubscriptionDetails | null;
  returnTo: string | null;
  updatedAt: string;
}

export interface StoredSubscriptionSnapshot {
  accessBlocked: boolean;
  allowed: boolean;
  reason: string | null;
  message: string | null;
  supportEmail: string | null;
  planCode: string | null;
  status: string | null;
  startsAt: string | null;
  endsAt: string | null;
  enforcementEnabled: boolean;
  updatedAt: string;
}

const isBrowser = () => typeof window !== 'undefined';

export const normalizeSubscriptionDetails = (
  value: unknown,
): AuthSubscriptionDetails | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const record = value as Record<string, unknown>;
  return {
    allowed: record.allowed === true,
    reason: typeof record.reason === 'string' ? record.reason : null,
    planCode: typeof record.planCode === 'string' ? record.planCode : null,
    status: typeof record.status === 'string' ? record.status : null,
    startsAt: typeof record.startsAt === 'string' ? record.startsAt : null,
    endsAt: typeof record.endsAt === 'string' ? record.endsAt : null,
    enforcementEnabled: record.enforcementEnabled === true,
  };
};

const normalizeSubscriptionStatusResponse = (
  value: unknown,
): AuthSubscriptionStatusResponse | null => {
  const root = value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
  if (!root) {
    return null;
  }

  const payload = (root.data && typeof root.data === 'object'
    ? root.data
    : root) as Record<string, unknown>;

  return {
    subscription: normalizeSubscriptionDetails(payload.subscription),
    enforcementEnabled: payload.enforcementEnabled === true,
  };
};

const buildBlockedMessage = (
  reason: string | null | undefined,
  explicitMessage?: string | null,
): string => {
  if (explicitMessage?.trim()) {
    return explicitMessage.trim();
  }

  if (reason === 'not_started') {
    return 'Your subscription is not active yet. Please contact support to continue using CalorieScience.';
  }

  return DEFAULT_SUBSCRIPTION_BLOCKED_MESSAGE;
};

const buildSnapshotFromAuthStatus = (
  subscription: AuthSubscriptionStatus,
): StoredSubscriptionSnapshot => {
  const details = subscription.subscription;
  return {
    accessBlocked: subscription.accessBlocked === true,
    allowed: details?.allowed === true,
    reason: subscription.reason ?? details?.reason ?? null,
    message: subscription.message ?? null,
    supportEmail: subscription.supportEmail ?? DEFAULT_SUBSCRIPTION_SUPPORT_EMAIL,
    planCode: details?.planCode ?? null,
    status: details?.status ?? null,
    startsAt: details?.startsAt ?? null,
    endsAt: details?.endsAt ?? null,
    enforcementEnabled: details?.enforcementEnabled === true,
    updatedAt: new Date().toISOString(),
  };
};

const buildSnapshotFromStatusResponse = (
  response: AuthSubscriptionStatusResponse,
): StoredSubscriptionSnapshot | null => {
  if (!response.subscription) {
    return null;
  }

  const details = response.subscription;
  const accessBlocked = response.enforcementEnabled && !details.allowed;

  return {
    accessBlocked,
    allowed: details.allowed === true,
    reason: details.reason,
    message: accessBlocked ? buildBlockedMessage(details.reason) : null,
    supportEmail: DEFAULT_SUBSCRIPTION_SUPPORT_EMAIL,
    planCode: details.planCode,
    status: details.status,
    startsAt: details.startsAt,
    endsAt: details.endsAt,
    enforcementEnabled: response.enforcementEnabled,
    updatedAt: new Date().toISOString(),
  };
};

export const readSubscriptionSnapshot = (): StoredSubscriptionSnapshot | null => {
  if (!isBrowser()) {
    return null;
  }

  try {
    const raw = localStorage.getItem(SUBSCRIPTION_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<StoredSubscriptionSnapshot>;
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    return {
      accessBlocked: parsed.accessBlocked === true,
      allowed: parsed.allowed === true,
      reason: typeof parsed.reason === 'string' ? parsed.reason : null,
      message: typeof parsed.message === 'string' ? parsed.message : null,
      supportEmail: typeof parsed.supportEmail === 'string' ? parsed.supportEmail : null,
      planCode: typeof parsed.planCode === 'string' ? parsed.planCode : null,
      status: typeof parsed.status === 'string' ? parsed.status : null,
      startsAt: typeof parsed.startsAt === 'string' ? parsed.startsAt : null,
      endsAt: typeof parsed.endsAt === 'string' ? parsed.endsAt : null,
      enforcementEnabled: parsed.enforcementEnabled === true,
      updatedAt:
        typeof parsed.updatedAt === 'string'
          ? parsed.updatedAt
          : new Date().toISOString(),
    };
  } catch {
    return null;
  }
};

export const persistSubscriptionSnapshot = (snapshot: StoredSubscriptionSnapshot) => {
  if (!isBrowser()) {
    return;
  }

  localStorage.setItem(SUBSCRIPTION_STORAGE_KEY, JSON.stringify(snapshot));
};

export const clearSubscriptionSnapshot = () => {
  if (!isBrowser()) {
    return;
  }

  localStorage.removeItem(SUBSCRIPTION_STORAGE_KEY);
};

export const clearSubscriptionSession = () => {
  clearSubscriptionGateState();
  clearSubscriptionSnapshot();
};

export const persistSubscriptionSnapshotFromAuthStatus = (
  subscription: AuthSubscriptionStatus | null | undefined,
) => {
  if (!subscription) {
    return;
  }

  persistSubscriptionSnapshot(buildSnapshotFromAuthStatus(subscription));
};

export const persistSubscriptionSnapshotFromStatusResponse = (
  response: AuthSubscriptionStatusResponse | null | undefined,
) => {
  if (!response) {
    return;
  }

  const snapshot = buildSnapshotFromStatusResponse(response);
  if (snapshot) {
    persistSubscriptionSnapshot(snapshot);
  }
};

export const readSubscriptionGateState = (): SubscriptionGateState | null => {
  if (!isBrowser()) {
    return null;
  }

  try {
    const raw = localStorage.getItem(SUBSCRIPTION_GATE_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<SubscriptionGateState>;
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    return {
      accessBlocked: Boolean(parsed.accessBlocked),
      reason: typeof parsed.reason === 'string' ? parsed.reason : null,
      message: typeof parsed.message === 'string' ? parsed.message : null,
      supportEmail: typeof parsed.supportEmail === 'string' ? parsed.supportEmail : null,
      subscription: normalizeSubscriptionDetails(parsed.subscription),
      returnTo: typeof parsed.returnTo === 'string' ? parsed.returnTo : null,
      updatedAt:
        typeof parsed.updatedAt === 'string'
          ? parsed.updatedAt
          : new Date().toISOString(),
    };
  } catch {
    return null;
  }
};

export const persistSubscriptionGateState = (
  input: Partial<SubscriptionGateState> & { accessBlocked: boolean },
) => {
  if (!isBrowser()) {
    return;
  }

  const previousState = readSubscriptionGateState();
  const nextState: SubscriptionGateState = {
    accessBlocked: input.accessBlocked,
    reason:
      typeof input.reason === 'string'
        ? input.reason
        : previousState?.reason ?? null,
    message:
      typeof input.message === 'string'
        ? input.message
        : previousState?.message ?? null,
    supportEmail:
      typeof input.supportEmail === 'string'
        ? input.supportEmail
        : previousState?.supportEmail ?? null,
    subscription:
      input.subscription !== undefined
        ? normalizeSubscriptionDetails(input.subscription)
        : previousState?.subscription ?? null,
    returnTo:
      typeof input.returnTo === 'string'
        ? input.returnTo
        : previousState?.returnTo ?? null,
    updatedAt: new Date().toISOString(),
  };

  localStorage.setItem(SUBSCRIPTION_GATE_STORAGE_KEY, JSON.stringify(nextState));
};

export const clearSubscriptionGateState = () => {
  if (!isBrowser()) {
    return;
  }
  localStorage.removeItem(SUBSCRIPTION_GATE_STORAGE_KEY);
};

export const notifySubscriptionAccessBlocked = (returnTo: string | null = null) => {
  if (!isBrowser()) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(SUBSCRIPTION_ACCESS_BLOCKED_EVENT, {
      detail: { returnTo },
    }),
  );
};

export const isSubscriptionRequiredApiError = (error: unknown): boolean => {
  if (!(error instanceof AxiosError)) {
    return false;
  }

  const status = error.response?.status;
  if (status === 402) {
    return true;
  }

  if (status !== 403) {
    return false;
  }

  const data = error.response.data;
  if (!data || typeof data !== 'object') {
    return false;
  }

  const errorCode = (data as Record<string, unknown>).error;
  return errorCode === 'Subscription required';
};

export const isProtectedSubscriptionApiRequest = (
  url: string | undefined,
  hasToken: boolean,
): boolean => {
  if (!hasToken || !url) {
    return false;
  }

  if (url.includes('/questionnaires/share/') || url.includes('/appointments-public/')) {
    return false;
  }

  if (url.includes('/auth/')) {
    return false;
  }

  return true;
};

export const syncSubscriptionGateFromAuthStatus = (
  subscription: AuthSubscriptionStatus | null | undefined,
  returnTo: string | null = null,
): boolean => {
  if (!subscription) {
    return false;
  }

  persistSubscriptionSnapshotFromAuthStatus(subscription);

  if (!subscription.accessBlocked) {
    clearSubscriptionGateState();
    return false;
  }

  persistSubscriptionGateState({
    accessBlocked: true,
    reason: subscription.reason ?? subscription.subscription?.reason ?? null,
    message: buildBlockedMessage(
      subscription.reason ?? subscription.subscription?.reason,
      subscription.message,
    ),
    supportEmail: subscription.supportEmail ?? DEFAULT_SUBSCRIPTION_SUPPORT_EMAIL,
    subscription: subscription.subscription ?? null,
    returnTo,
  });
  notifySubscriptionAccessBlocked(returnTo);
  return true;
};

export const syncSubscriptionGateFromStatusResponse = (
  response: AuthSubscriptionStatusResponse | null | undefined,
  returnTo: string | null = null,
): boolean => {
  persistSubscriptionSnapshotFromStatusResponse(response);

  if (!response?.enforcementEnabled || !response.subscription) {
    clearSubscriptionGateState();
    return false;
  }

  if (response.subscription.allowed) {
    clearSubscriptionGateState();
    return false;
  }

  const reason = response.subscription.reason;
  persistSubscriptionGateState({
    accessBlocked: true,
    reason,
    message: buildBlockedMessage(reason),
    supportEmail: DEFAULT_SUBSCRIPTION_SUPPORT_EMAIL,
    subscription: response.subscription,
    returnTo,
  });
  notifySubscriptionAccessBlocked(returnTo);
  return true;
};

export const syncSubscriptionGateFromApiError = (
  error: unknown,
  requestUrl?: string,
  returnTo: string | null = null,
  hasToken = false,
): boolean => {
  if (!(error instanceof AxiosError)) {
    return false;
  }

  const status = error.response?.status;

  if (isSubscriptionRequiredApiError(error)) {
    const data = (error.response?.data ?? {}) as Record<string, unknown>;
    const details = normalizeSubscriptionDetails(data.details);
    const reason = details?.reason
      ?? (typeof data.reason === 'string' ? data.reason : null)
      ?? (status === 402 ? 'expired' : null);

    persistSubscriptionGateState({
      accessBlocked: true,
      reason,
      message: buildBlockedMessage(
        reason,
        typeof data.message === 'string' ? data.message : null,
      ),
      supportEmail:
        typeof data.supportEmail === 'string'
          ? data.supportEmail
          : DEFAULT_SUBSCRIPTION_SUPPORT_EMAIL,
      subscription: details,
      returnTo,
    });
    if (details) {
      persistSubscriptionSnapshot({
        accessBlocked: true,
        allowed: details.allowed === true,
        reason: details.reason,
        message: typeof data.message === 'string' ? data.message : buildBlockedMessage(details.reason),
        supportEmail:
          typeof data.supportEmail === 'string'
            ? data.supportEmail
            : DEFAULT_SUBSCRIPTION_SUPPORT_EMAIL,
        planCode: details.planCode,
        status: details.status,
        startsAt: details.startsAt,
        endsAt: details.endsAt,
        enforcementEnabled: details.enforcementEnabled,
        updatedAt: new Date().toISOString(),
      });
    }
    notifySubscriptionAccessBlocked(returnTo);
    return true;
  }

  if (status === 401 && isProtectedSubscriptionApiRequest(requestUrl, hasToken)) {
    const previousSnapshot = readSubscriptionSnapshot();
    persistSubscriptionGateState({
      accessBlocked: true,
      reason: 'expired',
      message: DEFAULT_SUBSCRIPTION_BLOCKED_MESSAGE,
      supportEmail: DEFAULT_SUBSCRIPTION_SUPPORT_EMAIL,
      subscription: readSubscriptionGateState()?.subscription ?? null,
      returnTo,
    });
    persistSubscriptionSnapshot({
      accessBlocked: true,
      allowed: false,
      reason: previousSnapshot?.reason ?? 'expired',
      message: DEFAULT_SUBSCRIPTION_BLOCKED_MESSAGE,
      supportEmail: DEFAULT_SUBSCRIPTION_SUPPORT_EMAIL,
      planCode: previousSnapshot?.planCode ?? null,
      status: previousSnapshot?.status ?? null,
      startsAt: previousSnapshot?.startsAt ?? null,
      endsAt: previousSnapshot?.endsAt ?? null,
      enforcementEnabled: previousSnapshot?.enforcementEnabled ?? true,
      updatedAt: new Date().toISOString(),
    });
    notifySubscriptionAccessBlocked(returnTo);
    return true;
  }

  return false;
};

export const parseAuthSubscriptionStatusResponse = (
  value: unknown,
): AuthSubscriptionStatusResponse | null => normalizeSubscriptionStatusResponse(value);
